import { useState, useRef, useEffect } from 'react';
import {
    REPORT_MENU,
    generateDailyCollection,
    generateReportCards,
    type DailyCollectionRow,
    type ReportCardData,
} from './reportsMockData';
import { fetchMonthlyAttendanceReport, type MonthlyAttendanceRecord, type MonthlyAttendanceMeta } from '../../services/attendanceApi';
import './ReportCenter.css';
import { useTranslation } from 'react-i18next';
import { useCurrentSchoolName } from '../../hooks/useCurrentSchoolName';
import { fetchClasses, type ClassApiRecord } from '../../services/classApi';
import { fetchTransportRoutes, fetchRouteManifest, type TransportRouteRecord, type RouteManifestApiResponse } from '../../services/transportRouteApi';

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type ReportId = 'dailyCollection' | 'reportCards' | 'monthlyAttendance' | 'routeManifest';

export default function ReportCenter() {
    const { t: _t } = useTranslation();
    const schoolName = useCurrentSchoolName();
    const [selectedReport, setSelectedReport] = useState<ReportId | ''>('');
    const [loading, setLoading] = useState(false);
    const [generated, setGenerated] = useState(false);

    /* Param state */
    const [paramDate, setParamDate] = useState(new Date().toISOString().slice(0, 10));
    const [paramMonth, setParamMonth] = useState(new Date().toISOString().slice(0, 7));
    const [paramClass, setParamClass] = useState('all');

    /* Classes from API */
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    useEffect(() => {
        fetchClasses({ perPage: 100 }).then((res) => setClasses(res.data)).catch(() => {});
    }, []);

    /* Routes from API */
    const [routes, setRoutes] = useState<TransportRouteRecord[]>([]);
    const [paramRoute, setParamRoute] = useState('');
    useEffect(() => {
        fetchTransportRoutes().then((data) => {
            setRoutes(data);
            if (data.length > 0) setParamRoute(data[0].id);
        }).catch(() => {});
    }, []);

    /* Report data */
    const [dailyData, setDailyData] = useState<DailyCollectionRow[]>([]);
    const [attendanceData, setAttendanceData] = useState<MonthlyAttendanceRecord[]>([]);
    const [_attendanceMeta, setAttendanceMeta] = useState<MonthlyAttendanceMeta | null>(null);
    const [reportCardData, setReportCardData] = useState<ReportCardData[]>([]);
    const [manifestData, setManifestData] = useState<RouteManifestApiResponse | null>(null);

    const printRef = useRef<HTMLDivElement>(null);

    const reportMeta = REPORT_MENU.flatMap((g) => g.reports).find((r) => r.id === selectedReport);

    /* ── Generate ── */
    const handleGenerate = async () => {
        if (!selectedReport) return;
        setLoading(true);
        setGenerated(false);
        switch (selectedReport) {
            case 'dailyCollection': setDailyData(await generateDailyCollection()); break;
            case 'monthlyAttendance': {
                const res = await fetchMonthlyAttendanceReport({ month: paramMonth, class_id: paramClass });
                setAttendanceData(res.data);
                setAttendanceMeta(res.meta);
                break;
            }
            case 'reportCards': setReportCardData(await generateReportCards()); break;
            case 'routeManifest': if (paramRoute) setManifestData(await fetchRouteManifest(paramRoute)); break;
        }
        setLoading(false);
        setGenerated(true);
    };

    /* ── Print ── */
    const handlePrint = () => {
        if (!printRef.current) return;
        const w = window.open('', '_blank', 'width=900,height=700');
        if (!w) return;
        const now = new Date().toLocaleString('en-IN');
        w.document.write(`<!DOCTYPE html><html><head><title>${reportMeta?.name ?? 'Report'}</title>
<style>
  @page { margin: 12mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1e293b; font-size: 11px; }
  .print-header { text-align: center; border-bottom: 2px solid #1e293b; padding-bottom: 10px; margin-bottom: 14px; }
  .print-header h1 { font-size: 16px; font-weight: 800; }
  .print-header .sub { font-size: 9px; color: #6b7280; margin-top: 2px; }
  .print-header h2 { font-size: 13px; margin-top: 8px; text-transform: uppercase; letter-spacing: 0.08em; color: #374151; }
  .print-meta { font-size: 9px; color: #6b7280; margin-bottom: 10px; text-align: right; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  thead { display: table-header-group; }
  th { background: #f3f4f6; padding: 4px 6px; text-align: left; font-weight: 700; font-size: 9px; text-transform: uppercase; border-bottom: 1px solid #d1d5db; }
  td { padding: 3px 6px; border-bottom: 1px solid #e5e7eb; }
  .text-right { text-align: right; }
  .tabnum { font-variant-numeric: tabular-nums; }
  .total-row { font-weight: 800; background: #f3f4f6; }
  .report-card { page-break-after: always; border: 1px solid #d1d5db; border-radius: 4px; padding: 16px; margin-bottom: 16px; }
  .report-card:last-child { page-break-after: auto; }
  .rc-head { display: flex; justify-content: space-between; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; }
  .rc-label { font-size: 9px; color: #6b7280; text-transform: uppercase; }
  .rc-value { font-size: 11px; font-weight: 600; }
  .rc-result { font-size: 14px; font-weight: 800; text-align: center; margin-top: 10px; }
  .stop-group { margin-bottom: 12px; }
  .stop-title { font-size: 11px; font-weight: 700; padding: 4px 6px; background: #f3f4f6; border-left: 3px solid #667eea; margin-bottom: 4px; }
  .matrix-cell { width: 28px; height: 28px; text-align: center; font-size: 9px; font-weight: 600; }
  .rpt__cell-badge { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 4px; font-size: 8px; font-weight: 700; }
  .cell-P, .rpt__cell--P { color: #059669; }
  .rpt__cell--P .rpt__cell-badge { background: rgba(5,150,105,0.12); color: #059669; }
  .cell-A, .rpt__cell--A { color: #dc2626; }
  .rpt__cell--A .rpt__cell-badge { background: rgba(220,38,38,0.10); color: #dc2626; }
  .cell-L, .rpt__cell--L { color: #d97706; }
  .rpt__cell--L .rpt__cell-badge { background: rgba(217,119,6,0.10); color: #d97706; }
  .rpt__cell--Lt { color: #ea580c; }
  .rpt__cell--Lt .rpt__cell-badge { background: rgba(234,88,12,0.10); color: #ea580c; }
  .rpt__cell--H { color: #2563eb; }
  .rpt__cell--H .rpt__cell-badge { background: rgba(37,99,235,0.10); color: #2563eb; }
  .rpt__cell--Ho { color: #7c3aed; }
  .rpt__cell--Ho .rpt__cell-badge { background: rgba(124,58,237,0.10); color: #7c3aed; }
</style>
</head><body>
  <div class="print-header">
    <h1>${schoolName || 'Gurukul Academy'}</h1>
    <p class="sub">An Institution of Excellence</p>
    <h2>${reportMeta?.name ?? 'Report'}</h2>
  </div>
  <div class="print-meta">Generated on: ${now}</div>
  ${printRef.current.innerHTML}
</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 300);
    };

    /* ── CSV Export ── */
    const handleCSV = () => {
        let csv = '';
        if (selectedReport === 'dailyCollection') {
            csv = 'Receipt No,Student,Class,Amount,Method,Time\n';
            dailyData.forEach((r) => csv += `${r.receipt_no},"${r.student_name}",${r.class_name},${r.amount},${r.payment_method},${r.time}\n`);
        } else if (selectedReport === 'monthlyAttendance') {
            const dayKeys = attendanceData.length > 0 ? Object.keys(attendanceData[0].days) : [];
            csv = 'Roll,Student,Section,' + dayKeys.map((k) => k.slice(-2)).join(',') + ',Present,Absent,Leave\n';
            attendanceData.forEach((r) => {
                const days = dayKeys.map((k) => statusInitial(r.days[k])).join(',');
                const present = Object.values(r.days).filter((v) => statusInitial(v) === 'P').length;
                const absent = Object.values(r.days).filter((v) => statusInitial(v) === 'A').length;
                const leave = Object.values(r.days).filter((v) => statusInitial(v) === 'L').length;
                csv += `${r.roll_number},"${r.student.first_name} ${r.student.last_name ?? ''}","${r.section?.name ?? ''}",${days},${present},${absent},${leave}\n`;
            });
        }
        if (!csv) return;
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${selectedReport}_${paramDate}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    /* ═══ Report renderers ═══ */

    const renderDailyCollection = () => {
        const total = dailyData.reduce((s, r) => s + r.amount, 0);
        return (
            <div ref={printRef}>
                <table className="rpt__table">
                    <thead><tr><th className="rpt__th">Receipt No.</th><th className="rpt__th">Student</th><th className="rpt__th">Class</th><th className="rpt__th rpt__th--amount">Amount</th><th className="rpt__th">Method</th><th className="rpt__th">Time</th></tr></thead>
                    <tbody>
                        {dailyData.map((r, i) => (
                            <tr className="rpt__row" key={i}>
                                <td className="rpt__td rpt__td--mono">{r.receipt_no}</td>
                                <td className="rpt__td rpt__td--name">{r.student_name}</td>
                                <td className="rpt__td">{r.class_name}</td>
                                <td className="rpt__td rpt__td--amount">{formatCurrency(r.amount)}</td>
                                <td className="rpt__td"><span className="rpt__method-tag">{r.payment_method}</span></td>
                                <td className="rpt__td rpt__td--time">{r.time}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot><tr className="rpt__total-row"><td className="rpt__td" colSpan={3}><strong>Total Collection</strong></td><td className="rpt__td rpt__td--amount rpt__td--total">{formatCurrency(total)}</td><td className="rpt__td" colSpan={2}>{dailyData.length} receipts</td></tr></tfoot>
                </table>
            </div>
        );
    };

    /* Map any status value from the API (full word or abbreviation) to a single-letter initial */
    const statusInitial = (raw: string | null): string => {
        if (!raw) return '—';
        const key = raw.trim().toLowerCase();
        const map: Record<string, string> = {
            present: 'P', p: 'P',
            absent: 'A',  a: 'A',
            leave: 'L',   l: 'L',
            late: 'Lt',   lt: 'Lt',
            half_day: 'H', h: 'H',
            holiday: 'Ho', ho: 'Ho',
        };
        return map[key] ?? raw.charAt(0).toUpperCase();
    };

    const renderAttendance = () => {
        const dayKeys = attendanceData.length > 0 ? Object.keys(attendanceData[0].days) : [];
        return (
            <div ref={printRef} className="rpt__matrix-wrap">
                <table className="rpt__table rpt__table--matrix">
                    <thead>
                        <tr>
                            <th className="rpt__th rpt__th--sticky-col">Roll</th>
                            <th className="rpt__th rpt__th--sticky-col rpt__th--name-col">Student</th>
                            <th className="rpt__th rpt__th--sticky-col">Sec</th>
                            {dayKeys.map((k) => <th key={k} className="rpt__th rpt__th--day">{parseInt(k.slice(-2), 10)}</th>)}
                            <th className="rpt__th rpt__th--agg">P</th>
                            <th className="rpt__th rpt__th--agg">A</th>
                            <th className="rpt__th rpt__th--agg">L</th>
                        </tr>
                    </thead>
                    <tbody>
                        {attendanceData.map((r, i) => {
                            const present = Object.values(r.days).filter((v) => statusInitial(v) === 'P').length;
                            const absent = Object.values(r.days).filter((v) => statusInitial(v) === 'A').length;
                            const leave = Object.values(r.days).filter((v) => statusInitial(v) === 'L').length;
                            return (
                                <tr className="rpt__row" key={i}>
                                    <td className="rpt__td rpt__td--roll">{r.roll_number}</td>
                                    <td className="rpt__td rpt__td--name">{r.student.first_name} {r.student.last_name ?? ''}</td>
                                    <td className="rpt__td">{r.section?.name ?? '—'}</td>
                                    {dayKeys.map((k) => {
                                        const raw = r.days[k];
                                        const initial = statusInitial(raw);
                                        const cls = initial && initial !== '—' ? ` rpt__cell--${initial}` : '';
                                        return <td key={k} className={`rpt__td rpt__td--cell${cls}`}><span className="rpt__cell-badge">{initial}</span></td>;
                                    })}
                                    <td className="rpt__td rpt__td--agg rpt__cell--P">{present}</td>
                                    <td className="rpt__td rpt__td--agg rpt__cell--A">{absent}</td>
                                    <td className="rpt__td rpt__td--agg rpt__cell--L">{leave}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderReportCards = () => (
        <div ref={printRef} className="rpt__cards-wrap">
            {reportCardData.map((s, si) => (
                <div key={si} className="rpt__report-card">
                    <div className="rpt__rc-header">
                        <div className="rpt__rc-school">
                            <h3 className="rpt__rc-school-name">{schoolName || 'Gurukul Academy'}</h3>
                            <p className="rpt__rc-school-sub">Annual Examination</p>
                        </div>
                    </div>
                    <div className="rpt__rc-info">
                        <div className="rpt__rc-field"><span className="rpt__rc-label">Student Name</span><span className="rpt__rc-value">{s.student_name}</span></div>
                        <div className="rpt__rc-field"><span className="rpt__rc-label">Enrollment</span><span className="rpt__rc-value">{s.enrollment_number}</span></div>
                        <div className="rpt__rc-field"><span className="rpt__rc-label">Class / Section</span><span className="rpt__rc-value">{s.class_name} — {s.section_name}</span></div>
                        <div className="rpt__rc-field"><span className="rpt__rc-label">Roll No.</span><span className="rpt__rc-value">{s.roll_number}</span></div>
                    </div>
                    <table className="rpt__table rpt__table--rc">
                        <thead><tr><th className="rpt__th">Subject</th><th className="rpt__th rpt__th--amount">Max</th><th className="rpt__th rpt__th--amount">Obtained</th><th className="rpt__th">Grade</th></tr></thead>
                        <tbody>
                            {s.marks.map((m, mi) => (
                                <tr className="rpt__row" key={mi}><td className="rpt__td">{m.subject}</td><td className="rpt__td rpt__td--amount">{m.max_marks}</td><td className="rpt__td rpt__td--amount">{m.obtained}</td><td className="rpt__td"><span className={`rpt__grade rpt__grade--${m.grade.replace('+', 'p')}`}>{m.grade}</span></td></tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="rpt__total-row"><td className="rpt__td"><strong>Total</strong></td><td className="rpt__td rpt__td--amount">{s.total_max}</td><td className="rpt__td rpt__td--amount rpt__td--total">{s.total_obtained}</td><td className="rpt__td"></td></tr>
                        </tfoot>
                    </table>
                    <div className="rpt__rc-result-bar">
                        <span>Percentage: <strong>{s.percentage}%</strong></span>
                        <span>Rank: <strong>#{s.rank}</strong></span>
                        <span className={`rpt__rc-result rpt__rc-result--${s.result.toLowerCase()}`}>{s.result}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderRouteManifest = () => {
        if (!manifestData) return null;
        const totalStudents = manifestData.stops.reduce((s, st) => s + st.students.length, 0);
        const driverName = manifestData.driver
            ? `${manifestData.driver.first_name}${manifestData.driver.last_name ? ' ' + manifestData.driver.last_name : ''}`
            : '—';
        return (
            <div ref={printRef}>
                <div className="rpt__manifest-header">
                    <div className="rpt__manifest-field"><span className="rpt__manifest-label">Route</span><span className="rpt__manifest-val">{manifestData.title}</span></div>
                    <div className="rpt__manifest-field"><span className="rpt__manifest-label">Vehicle</span><span className="rpt__manifest-val">{manifestData.vehicle?.vehicle_number ?? '—'}</span></div>
                    <div className="rpt__manifest-field"><span className="rpt__manifest-label">Driver</span><span className="rpt__manifest-val">{driverName}</span></div>
                    <div className="rpt__manifest-field"><span className="rpt__manifest-label">Total Students</span><span className="rpt__manifest-val">{totalStudents}</span></div>
                </div>
                {manifestData.stops.map((routeStop, si) => (
                    <div key={si} className="rpt__stop-group">
                        <div className="rpt__stop-title">
                            <span className="rpt__stop-name">{routeStop.stop.name}</span>
                            <span className="rpt__stop-time">{routeStop.pickup_time}</span>
                            <span className="rpt__stop-count">{routeStop.students.length} students</span>
                        </div>
                        <table className="rpt__table">
                            <thead><tr><th className="rpt__th">#</th><th className="rpt__th">Student</th><th className="rpt__th">Class</th><th className="rpt__th">Parent Phone</th><th className="rpt__th rpt__th--check">✓</th></tr></thead>
                            <tbody>
                                {routeStop.students.length === 0 ? (
                                    <tr><td className="rpt__td" colSpan={5} style={{ textAlign: 'center', color: '#9ca3af' }}>No students assigned to this stop</td></tr>
                                ) : routeStop.students.map((s, i) => (
                                    <tr className="rpt__row" key={i}>
                                        <td className="rpt__td rpt__td--roll">{i + 1}</td>
                                        <td className="rpt__td rpt__td--name">{s.name}</td>
                                        <td className="rpt__td">{s.class_name ?? '—'}</td>
                                        <td className="rpt__td">{s.parent_phone ? <a href={`tel:${s.parent_phone}`} className="rpt__link">{s.parent_phone}</a> : '—'}</td>
                                        <td className="rpt__td rpt__td--check"><span className="rpt__checkbox" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        );
    };

    const renderReport = () => {
        if (loading) return <div className="rpt__loading"><div className="rpt__loader"><span className="rpt__loader-dot" /><span className="rpt__loader-dot" /><span className="rpt__loader-dot" /></div></div>;
        if (!generated) return null;
        switch (selectedReport) {
            case 'dailyCollection': return renderDailyCollection();
            case 'monthlyAttendance': return renderAttendance();
            case 'reportCards': return renderReportCards();
            case 'routeManifest': return renderRouteManifest();
            default: return null;
        }
    };

    return (
        <div className="rpt">
            {/* Left pane — menu */}
            <aside className="rpt__menu">
                <h2 className="rpt__menu-title">Reports</h2>
                {REPORT_MENU.map((g) => (
                    <div key={g.title} className="rpt__menu-group">
                        <span className="rpt__menu-group-title">{g.title}</span>
                        {g.reports.map((r) => (
                            <button
                                key={r.id}
                                className={`rpt__menu-item ${selectedReport === r.id ? 'rpt__menu-item--active' : ''}`}
                                onClick={() => {
                                    const id = r.id as ReportId;
                                    setSelectedReport(id);
                                    setGenerated(false);
                                    if (id === 'monthlyAttendance' && paramClass === 'all') setParamClass('');
                                }}
                            >
                                <span className="rpt__menu-item-name">{r.name}</span>
                                <span className="rpt__menu-item-desc">{r.description}</span>
                            </button>
                        ))}
                    </div>
                ))}
            </aside>

            {/* Right pane — viewer */}
            <div className="rpt__viewer">
                {!selectedReport ? (
                    <div className="rpt__placeholder">
                        <svg className="rpt__placeholder-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                        <h3 className="rpt__placeholder-title">Select a Report</h3>
                        <p className="rpt__placeholder-text">Choose a report from the menu to configure and generate it.</p>
                    </div>
                ) : (
                    <>
                        {/* Action bar */}
                        <div className="rpt__action-bar">
                            <h2 className="rpt__report-title">{reportMeta?.name}</h2>
                            {generated && (
                                <div className="rpt__action-btns">
                                    <button className="rpt__action-btn" onClick={handlePrint}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                                        Print
                                    </button>
                                    <button className="rpt__action-btn" onClick={handlePrint}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
                                        Export PDF
                                    </button>
                                    <button className="rpt__action-btn" onClick={handleCSV}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                        Export CSV
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Parameter gateway */}
                        <div className="rpt__params">
                            {selectedReport === 'monthlyAttendance' ? (
                                <div className="rpt__param-field">
                                    <label className="rpt__param-label">Month</label>
                                    <input className="rpt__param-input" type="month" value={paramMonth} onChange={(e) => setParamMonth(e.target.value)} />
                                </div>
                            ) : selectedReport !== 'routeManifest' && (
                                <div className="rpt__param-field">
                                    <label className="rpt__param-label">Date</label>
                                    <input className="rpt__param-input" type="date" value={paramDate} onChange={(e) => setParamDate(e.target.value)} />
                                </div>
                            )}
                            {selectedReport === 'monthlyAttendance' && (
                                <div className="rpt__param-field">
                                    <label className="rpt__param-label">Class <span className="rpt__param-required">*</span></label>
                                    <select className="rpt__param-select" value={paramClass} onChange={(e) => setParamClass(e.target.value)}>
                                        <option value="">Select class…</option>
                                        {classes.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {selectedReport !== 'routeManifest' && selectedReport !== 'monthlyAttendance' && (
                                <div className="rpt__param-field">
                                    <label className="rpt__param-label">Class</label>
                                    <select className="rpt__param-select" value={paramClass} onChange={(e) => setParamClass(e.target.value)}>
                                        <option value="all">All Classes</option>
                                        {classes.map((c) => (
                                            <option key={c.id} value={c.id}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {selectedReport === 'routeManifest' && (
                                <div className="rpt__param-field">
                                    <label className="rpt__param-label">Route</label>
                                    <select className="rpt__param-select" value={paramRoute} onChange={(e) => setParamRoute(e.target.value)}>
                                        {routes.length === 0 && <option value="">No routes available</option>}
                                        {routes.map((r) => (
                                            <option key={r.id} value={r.id}>{r.title}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <button
                                className="rpt__generate-btn"
                                onClick={handleGenerate}
                                disabled={loading || (selectedReport === 'monthlyAttendance' && !paramClass)}
                            >
                                {loading ? (
                                    <><svg className="rpt__spinner" width="16" height="16" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" strokeWidth="3" /></svg> Generating…</>
                                ) : (
                                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3" /></svg> Generate Report</>
                                )}
                            </button>
                        </div>

                        {/* Report preview */}
                        <div className="rpt__preview">
                            {renderReport()}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
